import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBagShopping, faFire, faHeart, faPlus, faStar } from '@fortawesome/free-solid-svg-icons'
import MinimumQuantity from '../minimum-quantity/MinimumQuantity'
import QuantityInput from '../quantity-input/QuantityInput'
import { IProduct } from '../../types/products'
import { useAppType } from '../../context/AppContext'
import { useInView } from '../../utils/useInView'
import { imageUrl, imageSrcSet, imageLqip } from '../../utils/imageUrl'
import { handleImageFallback } from '../../utils/brand'
import {
  addProductToCart,
  discountedPrice,
  quantityInCart,
  setCartQuantity,
} from '../../utils/cart'
import { flyToCart, floatText, haptic, iconBurst } from '../../utils/fx'
import { useOpenProduct } from '../../utils/openProduct'
import './MenuItem.scss'

type Props = {
  data?: IProduct
  setCart: React.Dispatch<React.SetStateAction<IProduct[]>>
  cart: IProduct[]
  compact?: boolean
  index?: number
}

const c = 'menu-item'
const BIG_APPETITE = 10

export function productName(product?: IProduct) {
  return i18next.language === 'ru' && product?.ru_name
    ? product.ru_name
    : product?.name
}

export function productMeta(product: IProduct | undefined, piecesLabel: string) {
  return [
    product?.weight ? `${product.weight}g` : '',
    product?.pieces ? `${product.pieces} ${piecesLabel}` : '',
  ]
    .filter(Boolean)
    .join(' · ')
}

const MenuItem = ({ data, cart, setCart, compact, index = 0 }: Props) => {
  const openProduct = useOpenProduct()
  const appType = useAppType()
  const { t } = useTranslation()
  const [imageLoaded, setImageLoaded] = useState(false)
  const [mediaRef, inView] = useInView<HTMLButtonElement>('1000px 0px')
  const imgRef = useRef<HTMLImageElement>(null)
  const quantity = quantityInCart(cart, data?._id)
  const price = discountedPrice(data)

  function celebrateAdd(e: React.MouseEvent, next: number) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const x = rect.left + rect.width / 2
    if (next === BIG_APPETITE) {
      floatText(x, rect.top, t('BIG_APPETITE'))
      iconBurst(x, rect.top + rect.height / 2, [faFire, faHeart, faStar], 10)
    } else {
      floatText(x, rect.top, '+1')
    }
  }

  function addToCart(e: React.MouseEvent) {
    e.stopPropagation()
    if (!data) return
    haptic(12)
    setCart((prev) => addProductToCart(prev, data, 1))
    celebrateAdd(e, quantity + 1)
    flyToCart(imgRef.current, imageUrl(data.image, 240) || undefined)
  }

  function changeQuantity(next: number | ((prev: number) => number)) {
    if (!data) return
    const value = typeof next === 'function' ? next(quantity) : next
    setCart((prev) => setCartQuantity(prev, data._id, value))
    if (value > quantity) flyToCart(imgRef.current, imageUrl(data.image, 240) || undefined)
    if (value === BIG_APPETITE && imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect()
      floatText(rect.left + rect.width / 2, rect.top + rect.height / 2, t('BIG_APPETITE'))
    }
  }

  function open() {
    if (!data) return
    haptic()
    openProduct(data._id, mediaRef.current, data)
  }

  return (
    <article
      className={`${c}${compact ? ` ${c}--compact` : ''}${quantity ? ' is-in-cart' : ''}`}
      style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
    >
      <button
        className={`${c}__media`}
        ref={mediaRef}
        onClick={open}
        aria-label={productName(data)}
        style={
          inView && data?.image
            ? { backgroundImage: `url(${imageLqip(data.image)})` }
            : undefined
        }
      >
        <img
          ref={imgRef}
          src={inView ? imageUrl(data?.image, 480) : undefined}
          srcSet={inView ? imageSrcSet(data?.image, [240, 480]) : undefined}
          sizes='(max-width: 640px) 50vw, 240px'
          alt=''
          loading='lazy'
          decoding='async'
          className={imageLoaded ? 'is-loaded' : ''}
          onLoad={() => setImageLoaded(true)}
          onError={handleImageFallback(appType)}
          draggable={false}
        />
        <div className={`${c}__badges`}>
          {data?.isNew && (
            <span className={`${c}__badge ${c}__badge--new`}>{t('NEW')}</span>
          )}
          {data?.discount && (
            <span className={`${c}__badge ${c}__badge--sale`}>
              -{data.discount}%
            </span>
          )}
        </div>
        {quantity > 0 && (
          <span key={quantity} className={`${c}__in-cart`}>
            <FontAwesomeIcon icon={faBagShopping} /> {quantity}
          </span>
        )}
      </button>

      <div className={`${c}__body`}>
        <h3 className={`${c}__name`} onClick={open}>
          {productName(data)}
        </h3>
        {!compact && (
          <div className={`${c}__meta`}>
            {productMeta(data, t('PIECES'))}
          </div>
        )}
        <MinimumQuantity product={data} compact />

        <div className={`${c}__footer`}>
          <div className={`${c}__price`}>
            {data?.discount && <s>{data.price}</s>}
            <strong>
              {price}
              <small> lei</small>
            </strong>
          </div>
          {quantity > 0 ? (
            <QuantityInput
              quantity={quantity}
              setQuantity={changeQuantity}
              cartItem
              size='sm'
              tone='primary'
            />
          ) : (
            <button
              className={`${c}__add`}
              onClick={addToCart}
              aria-label={t('ADD_TO_CART')}
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>{t('ADD')}</span>
            </button>
          )}
        </div>
      </div>
    </article>
  )
}

export default MenuItem
